import React, { useCallback, useEffect, useState } from "react";
import { Plus, Search } from "lucide-react";
import {
  createCustomer,
  getCustomer,
  getSchedulingErrorMessage,
  searchCustomers,
  updateCustomer,
} from "../../../api/schedulingApi";
import { Alert, Field, Modal, StatusPill, btnPrimary, btnSecondary, card, formatDateTime, inputClass } from "./ui";

const EMPTY = { name: "", phone: "", email: "" };

export default function CustomersPanel({ botId, timeZone, providers }) {
  const [query, setQuery] = useState("");
  const [rows, setRows] = useState([]);
  const [error, setError] = useState("");
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [viewing, setViewing] = useState(null);

  const load = useCallback(async () => {
    setError("");
    try {
      setRows((await searchCustomers(botId, { q: query.trim() || undefined })) || []);
    } catch (err) {
      setError(getSchedulingErrorMessage(err, "Could not load customers."));
    }
  }, [botId, query]);

  useEffect(() => {
    const t = setTimeout(load, 300);
    return () => clearTimeout(t);
  }, [load]);

  useEffect(() => {
    if (editing) setForm({ name: editing.name || "", phone: editing.phone || "", email: editing.email || "" });
  }, [editing]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    const values = { name: form.name.trim() || null, phone: form.phone.trim() || null, email: form.email.trim() || null };
    try {
      if (editing.id) await updateCustomer(botId, editing.id, values);
      else await createCustomer(botId, values);
      setEditing(null);
      await load();
    } catch (err) {
      setError(getSchedulingErrorMessage(err, "Could not save customer."));
    }
  };

  const open = async (c) => {
    try {
      setViewing(await getCustomer(botId, c.id));
    } catch (err) {
      setError(getSchedulingErrorMessage(err));
    }
  };

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));
  const providerName = (id) => providers.find((p) => p.id === id)?.name || `#${id}`;

  return (
    <div className={`${card} space-y-4`}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="relative">
          <Search size={15} className="absolute left-3 top-2.5 text-slate-400" />
          <input className="w-72 rounded-xl border border-slate-200 py-2 pl-9 pr-3 text-sm" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search name, phone or email" aria-label="Search customers" />
        </div>
        <button type="button" className={btnPrimary} onClick={() => setEditing({})}><Plus size={16} /> Add customer</button>
      </div>
      <Alert>{error}</Alert>
      {rows.length === 0 ? (
        <p className="py-6 text-center text-sm text-slate-400">No customers found.</p>
      ) : (
        <table className="w-full text-left text-sm">
          <thead className="text-xs uppercase text-slate-500">
            <tr><th className="py-2">Name</th><th>Phone</th><th>Email</th><th /></tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {rows.map((c) => (
              <tr key={c.id}>
                <td className="py-2 font-semibold text-slate-900">{c.name || "Unnamed"}</td>
                <td>{c.phone || "—"}</td>
                <td>{c.email || "—"}</td>
                <td className="space-x-2 text-right">
                  <button type="button" className={btnSecondary} onClick={() => open(c)}>History</button>
                  <button type="button" className={btnSecondary} onClick={() => setEditing(c)}>Edit</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <Modal open={editing !== null} title={editing?.id ? "Edit customer" : "Add customer"} onClose={() => setEditing(null)}>
        <form className="space-y-4" onSubmit={submit}>
          <Field label="Name"><input required className={inputClass} value={form.name} onChange={set("name")} /></Field>
          <Field label="Phone" hint="Family members may share a number."><input className={inputClass} value={form.phone} onChange={set("phone")} /></Field>
          <Field label="Email"><input type="email" className={inputClass} value={form.email} onChange={set("email")} /></Field>
          <div className="flex justify-end gap-2">
            <button type="button" className={btnSecondary} onClick={() => setEditing(null)}>Cancel</button>
            <button type="submit" className={btnPrimary}>Save</button>
          </div>
        </form>
      </Modal>

      <Modal open={viewing !== null} title={`${viewing?.name || "Customer"} · appointments`} onClose={() => setViewing(null)} wide>
        {viewing?.appointments?.length ? (
          <ul className="divide-y divide-slate-100">
            {[...viewing.appointments].reverse().map((a) => (
              <li key={a.id} className="flex items-center justify-between py-2 text-sm">
                <span>{formatDateTime(a.start_at, timeZone)} · {providerName(a.provider_id)}</span>
                <StatusPill status={a.status} />
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-sm text-slate-400">No appointments yet.</p>
        )}
      </Modal>
    </div>
  );
}
